"use client";

import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { LucideIcon } from "lucide-react";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

type KpiCounterProps = {
  finalValue: string;
  label: string;
  /** Optional glyph rendered above the number — used by the instrument-strip treatment. */
  icon?: LucideIcon;
  /** Center the icon/number/label/underline within the cell instead of left-aligning them. */
  centered?: boolean;
};

function parseTarget(value: string) {
  const numeric = parseFloat(value.replace(/[^\d.]/g, ""));
  const suffix = value.replace(/[\d.,]/g, "");
  return { numeric: Number.isFinite(numeric) ? numeric : 0, suffix };
}

export function KpiCounter({ finalValue, label, icon: Icon, centered = false }: KpiCounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(finalValue);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const { numeric, suffix } = parseTarget(finalValue);
    const counter = { value: 0 };

    const ctx = gsap.context(() => {
      gsap.to(counter, {
        value: numeric,
        duration: 1.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 90%",
          once: true,
        },
        onUpdate: () => {
          setDisplay(`${Math.round(counter.value)}${suffix}`);
        },
      });
    }, el);

    return () => ctx.revert();
  }, [finalValue]);

  return (
    <div className={`group/kpi relative flex flex-col gap-3 ${centered ? "items-center text-center" : ""}`}>
      {Icon && (
        <Icon
          size={18}
          strokeWidth={1.75}
          className="text-[var(--color-primary-text)] opacity-70 transition-opacity duration-300 group-hover/kpi:opacity-100"
        />
      )}
      <span
        ref={ref}
        className="font-display text-4xl font-bold tabular-nums text-[var(--color-text-primary)] transition-colors duration-300 group-hover/kpi:text-[var(--color-primary-text)] sm:text-5xl"
        data-final={finalValue}
      >
        {display}
      </span>
      <span className="font-mono-tech text-xs uppercase tracking-[0.1em] text-[var(--color-text-tertiary)]">
        {label}
      </span>
      <span
        aria-hidden
        className={`absolute -bottom-4 h-px w-0 bg-[var(--color-primary-light)] transition-[width] duration-300 group-hover/kpi:w-8 ${centered ? "left-1/2 -translate-x-1/2" : "left-0"}`}
      />
    </div>
  );
}
